import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as eventsApi from '../api/energyhubEvents';

export function EnergyHubEvents() {
  const { selectedOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const canViewCompliance = hasPermission('compliance:read');
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [filters, setFilters] = useState({
    severity: '',
    type: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canViewCompliance) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const activeFilters = { limit: 50 };
      if (filters.severity) activeFilters.severity = filters.severity;
      if (filters.type) activeFilters.type = filters.type;
      
      const result = await eventsApi.getEvents(selectedOrg.id, activeFilters);
      setEvents(result.events);
      setTotal(result.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canViewCompliance, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRowClick = async (eventId) => {
    if (!selectedOrg) return;
    try {
      const event = await eventsApi.getEvent(selectedOrg.id, eventId);
      setSelectedEvent(event);
      setShowDrawer(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return { bg: '#fed7d7', color: '#c53030' };
      case 'warning': return { bg: '#fefcbf', color: '#975a16' };
      case 'info': return { bg: '#bee3f8', color: '#2b6cb0' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'sync': return { bg: '#c6f6d5', color: '#276749' };
      case 'api_error': return { bg: '#fed7d7', color: '#c53030' };
      case 'rate_limit': return { bg: '#fefcbf', color: '#975a16' };
      case 'demand_response': return { bg: '#e9d8fd', color: '#6b46c1' };
      case 'enrollment': return { bg: '#bee3f8', color: '#2b6cb0' };
      case 'telemetry': return { bg: '#b2f5ea', color: '#234e52' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!canViewCompliance) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need compliance permissions to view EnergyHub events.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="EnergyHub Event Log"
        subtitle="View sync logs, errors, and integration events"
      />

      <div style={styles.filterBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Severity</label>
          <select
            value={filters.severity}
            onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
            style={styles.filterSelect}
            data-testid="severity-filter"
          >
            <option value="">All</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Type</label>
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            style={styles.filterSelect}
            data-testid="type-filter"
          >
            <option value="">All</option>
            <option value="sync">Sync</option>
            <option value="api_error">API Error</option>
            <option value="rate_limit">Rate Limit</option>
            <option value="demand_response">Demand Response</option>
            <option value="enrollment">Enrollment</option>
            <option value="telemetry">Telemetry</option>
          </select>
        </div>
        <div style={styles.totalCount}>
          {total} event{total !== 1 ? 's' : ''}
        </div>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading events...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="events-table">
            <thead>
              <tr>
                <th style={styles.th}>Time</th>
                <th style={styles.th}>Severity</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Message</th>
                <th style={styles.th}>Correlation ID</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => {
                const sevStyle = getSeverityColor(event.severity);
                const typeStyle = getTypeColor(event.type);
                return (
                  <tr
                    key={event.id}
                    style={styles.tr}
                    onClick={() => handleRowClick(event.id)}
                    data-testid={`event-${event.id}`}
                  >
                    <td style={styles.td}>{new Date(event.timestamp).toLocaleString()}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: sevStyle.bg, color: sevStyle.color }}>
                        {event.severity}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: typeStyle.bg, color: typeStyle.color }}>
                        {event.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={styles.td}>{event.source}</td>
                    <td style={styles.td}>{event.message}</td>
                    <td style={styles.td}>
                      <code style={styles.code}>{event.correlationId}</code>
                    </td>
                  </tr>
                );
              })}
              {events.length === 0 && (
                <tr>
                  <td colSpan="6" style={styles.emptyState}>No events found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showDrawer && selectedEvent && (
        <div style={styles.drawerOverlay} onClick={() => setShowDrawer(false)}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()} data-testid="event-drawer">
            <div style={styles.drawerHeader}>
              <h3 style={styles.drawerTitle}>Event Details</h3>
              <button onClick={() => setShowDrawer(false)} style={styles.closeButton}>×</button>
            </div>
            
            <div style={styles.drawerContent}>
              <div style={styles.drawerSection}>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Event ID</span>
                  <code style={styles.code}>{selectedEvent.id}</code>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Timestamp</span>
                  <span>{new Date(selectedEvent.timestamp).toLocaleString()}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Severity</span>
                  <span style={{ ...styles.badge, ...getSeverityColor(selectedEvent.severity) }}>
                    {selectedEvent.severity}
                  </span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Type</span>
                  <span style={{ ...styles.badge, ...getTypeColor(selectedEvent.type) }}>
                    {selectedEvent.type.replace('_', ' ')}
                  </span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Source</span>
                  <span>{selectedEvent.source}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Correlation ID</span>
                  <code style={styles.code}>{selectedEvent.correlationId}</code>
                </div>
              </div>

              <div style={styles.drawerSection}>
                <h4 style={styles.sectionTitle}>Message</h4>
                <p style={styles.message}>{selectedEvent.message}</p>
              </div>

              <div style={styles.drawerSection}>
                <h4 style={styles.sectionTitle}>Payload</h4>
                <pre style={styles.payload} data-testid="event-payload">
                  {JSON.stringify(selectedEvent.payload, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  filterBar: { display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  filterLabel: { fontSize: '0.75rem', fontWeight: '500', color: '#4a5568' },
  filterSelect: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white', minWidth: '150px' },
  totalCount: { marginLeft: 'auto', fontSize: '0.875rem', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0', cursor: 'pointer', transition: 'background 0.15s' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  badge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  code: { background: '#f7fafc', padding: '0.125rem 0.25rem', borderRadius: '2px', fontSize: '0.75rem' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  drawerOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 },
  drawer: { width: '500px', maxWidth: '100%', background: 'white', height: '100%', overflow: 'auto', boxShadow: '-4px 0 6px rgba(0,0,0,0.1)' },
  drawerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0' },
  drawerTitle: { margin: 0, fontSize: '1.25rem', fontWeight: '600' },
  closeButton: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' },
  drawerContent: { padding: '1.5rem' },
  drawerSection: { marginBottom: '1.5rem' },
  drawerRow: { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc' },
  drawerLabel: { color: '#718096', fontSize: '0.875rem' },
  sectionTitle: { margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' },
  message: { margin: 0, fontSize: '0.875rem', color: '#2d3748' },
  payload: { margin: 0, padding: '1rem', background: '#f7fafc', borderRadius: '8px', fontSize: '0.75rem', overflow: 'auto', whiteSpace: 'pre-wrap' }
};
